import * as React from "react";
import { useNotify } from "react-admin";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import FlightIcon from "@material-ui/icons/Flight";
import { useAppDataProvider } from "../../../dataprovider";

const MONTH_NAMES = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - i);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

type MonthStatus = "idle" | "pending" | "success" | "error";

interface MonthRow {
  year: number;
  month: number;
  status: MonthStatus;
  errorMessage?: string;
}

function buildMonthRange(
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number
): Array<{ year: number; month: number }> {
  const result: Array<{ year: number; month: number }> = [];
  let y = startYear;
  let m = startMonth;
  while (y < endYear || (y === endYear && m <= endMonth)) {
    result.push({ year: y, month: m });
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return result;
}

const FlightRadarFetch: React.FC = () => {
  const notify = useNotify();
  const dataProvider = useAppDataProvider();

  const [startYear, setStartYear] = React.useState<number>(currentYear);
  const [startMonth, setStartMonth] = React.useState<number>(1);
  const [endYear, setEndYear] = React.useState<number>(currentYear);
  const [endMonth, setEndMonth] = React.useState<number>(new Date().getMonth() + 1);
  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const [monthStatuses, setMonthStatuses] = React.useState<MonthRow[]>([]);

  const isEndBeforeStart = endYear < startYear || (endYear === startYear && endMonth < startMonth);

  const handleFetch = async () => {
    if (isEndBeforeStart) return;

    const months = buildMonthRange(startYear, startMonth, endYear, endMonth);
    const initialStatuses: MonthRow[] = months.map(({ year, month }) => ({
      year,
      month,
      status: "idle",
    }));
    setMonthStatuses(initialStatuses);
    setIsFetching(true);

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < months.length; i++) {
      const { year, month } = months[i];

      setMonthStatuses(prev => prev.map((row, idx) => (idx === i ? { ...row, status: "pending" } : row)));

      try {
        await dataProvider.postFlightRadarFetch({ periodYear: year, periodMonth: month });
        setMonthStatuses(prev => prev.map((row, idx) => (idx === i ? { ...row, status: "success" } : row)));
        successCount++;
      } catch (error: any) {
        const message =
          error?.message || error?.body?.message || (typeof error === "string" ? error : "Bilinmeyen hata");
        setMonthStatuses(prev =>
          prev.map((row, idx) => (idx === i ? { ...row, status: "error", errorMessage: message } : row))
        );
        errorCount++;
      }
    }

    setIsFetching(false);

    const total = months.length;
    if (errorCount === 0) {
      notify(`Tüm ${total} ay başarıyla çekildi.`, "success");
    } else {
      notify(
        `${successCount} ay başarılı, ${errorCount} ay hatalı (Toplam: ${total}).`,
        errorCount === total ? "error" : "warning"
      );
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3} gridGap={8}>
        <FlightIcon color="primary" />
        <Typography variant="h5">Flight Radar Veri Çekme</Typography>
      </Box>

      <Paper elevation={2}>
        <Box p={3}>
          <Typography variant="subtitle1" gutterBottom>
            Dönem Aralığı Seçin
          </Typography>

          <Box display="flex" alignItems="flex-end" flexWrap="wrap" gridGap={16} mt={2}>
            {/* Başlangıç */}
            <Box>
              <Typography variant="caption" color="textSecondary">
                Başlangıç Dönemi
              </Typography>
              <Box display="flex" gridGap={8} mt={0.5}>
                <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
                  <InputLabel>Yıl</InputLabel>
                  <Select
                    value={startYear}
                    onChange={e => setStartYear(Number(e.target.value))}
                    label="Yıl"
                    disabled={isFetching}
                  >
                    {YEARS.map(y => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                  <InputLabel>Ay</InputLabel>
                  <Select
                    value={startMonth}
                    onChange={e => setStartMonth(Number(e.target.value))}
                    label="Ay"
                    disabled={isFetching}
                  >
                    {MONTHS.map(m => (
                      <MenuItem key={m} value={m}>
                        {MONTH_NAMES[m - 1]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Typography variant="h6" style={{ paddingBottom: 4 }}>
              —
            </Typography>

            {/* Bitiş */}
            <Box>
              <Typography variant="caption" color="textSecondary">
                Bitiş Dönemi
              </Typography>
              <Box display="flex" gridGap={8} mt={0.5}>
                <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
                  <InputLabel>Yıl</InputLabel>
                  <Select
                    value={endYear}
                    onChange={e => setEndYear(Number(e.target.value))}
                    label="Yıl"
                    disabled={isFetching}
                  >
                    {YEARS.map(y => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                  <InputLabel>Ay</InputLabel>
                  <Select
                    value={endMonth}
                    onChange={e => setEndMonth(Number(e.target.value))}
                    label="Ay"
                    disabled={isFetching}
                  >
                    {MONTHS.map(m => (
                      <MenuItem key={m} value={m}>
                        {MONTH_NAMES[m - 1]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleFetch}
              disabled={isFetching || isEndBeforeStart}
              startIcon={isFetching ? <CircularProgress size={16} color="inherit" /> : <FlightIcon />}
              style={{ alignSelf: "flex-end", height: 40 }}
            >
              {isFetching ? "Çekiliyor..." : "Fetch"}
            </Button>
          </Box>

          {isEndBeforeStart && (
            <Typography variant="caption" color="error" style={{ display: "block", marginTop: 8 }}>
              Bitiş dönemi başlangıç döneminden önce olamaz.
            </Typography>
          )}
        </Box>
      </Paper>

      {monthStatuses.length > 0 && (
        <Box mt={3}>
          <Paper elevation={2}>
            <Box p={2}>
              <Typography variant="subtitle1" gutterBottom>
                Ay Bazlı Durum
              </Typography>
              <List dense>
                {monthStatuses.map((row, idx) => (
                  <ListItem key={idx} divider>
                    <ListItemIcon style={{ minWidth: 36 }}>
                      {row.status === "idle" && <HourglassEmptyIcon style={{ color: "#9e9e9e" }} />}
                      {row.status === "pending" && <CircularProgress size={20} />}
                      {row.status === "success" && <CheckCircleOutlineIcon style={{ color: "#4caf50" }} />}
                      {row.status === "error" && <ErrorOutlineIcon style={{ color: "#f44336" }} />}
                    </ListItemIcon>
                    <ListItemText
                      primary={`${MONTH_NAMES[row.month - 1]} ${row.year}`}
                      secondary={
                        row.status === "idle"
                          ? "Bekliyor"
                          : row.status === "pending"
                            ? "İşleniyor..."
                            : row.status === "success"
                              ? "Başarılı"
                              : `Hata: ${row.errorMessage}`
                      }
                      secondaryTypographyProps={{
                        style: {
                          color: row.status === "success" ? "#4caf50" : row.status === "error" ? "#f44336" : undefined,
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default FlightRadarFetch;
